import { useLocale, useTranslations } from 'next-intl'
import { cx } from 'class-variance-authority'
import { Loader } from '@skop-app/lib-front'

import type { DocumensoSignDocument, Recipient } from '@/types'
import { type Activity, ActivityBar } from '@/components/molecules'

type SignNotificationsProps = {
  isLoading: boolean
  signRequest: DocumensoSignDocument
  className?: string
}

export const SignNotifications = ({
  isLoading,
  signRequest,
  className,
}: SignNotificationsProps) => {
  const t = useTranslations()
  const locale = useLocale()

  if (isLoading) {
    return (
      <div
        className={cx('flex min-h-80 items-center justify-center', className)}
      >
        <Loader />
      </div>
    )
  }

  const recipientActivities = signRequest?.recipients.map(
    (recipient: Recipient) => [
      ...(recipient.readStatus === 'OPENED'
        ? [
            {
              id: recipient.id + 'opened',
              icon: 'EnvelopeOpen',
              iconClassName: 'text-blue-500',
              iconWeight: 'bold',
              dotClassName: '!bg-blue-100',
              verticalLineClassName: '!bg-blue-100',
              title: t('Documents-directory.opened'),
              description:
                recipient.role === 'APPROVER'
                  ? t('Documents-directory.sign_approuval_opened', {
                      name: recipient.name,
                    })
                  : t('Documents-directory.sign_request_opened', {
                      name: recipient.name,
                    }),
            } as Activity,
          ]
        : []),
      ...(recipient.signingStatus === 'SIGNED' && recipient.role !== 'CC'
        ? [
            {
              id: recipient.id + 'signed',
              icon: recipient.role === 'APPROVER' ? 'Check' : 'Signature',
              iconWeight: recipient.role === 'APPROVER' ? 'bold' : 'fill',
              iconClassName: 'text-green-500',
              dotClassName: '!bg-green-100',
              verticalLineClassName: '!bg-green-100',
              title:
                recipient.role === 'APPROVER'
                  ? t('Documents-directory.sign_approuved')
                  : t('Documents-directory.sign_signed'),
              description:
                recipient.role === 'APPROVER'
                  ? t('Documents-directory.sign_request_approved', {
                      name: recipient.name,
                    })
                  : t('Documents-directory.sign_request_signed', {
                      name: recipient.name,
                    }),
              date: recipient.signedAt,
              dateFormat: 'date-time',
            } as Activity,
          ]
        : []),
      ...(recipient.signingStatus === 'REJECTED'
        ? [
            {
              id: recipient.id + 'rejected',
              icon: 'FileX',
              iconClassName: 'text-red-600',
              iconWeight: 'bold',
              dotClassName: '!bg-red-100',
              verticalLineClassName: '!bg-red-100',
              title: t('Review.rejected'),
              description:
                recipient.role === 'APPROVER'
                  ? t('Documents-directory.sign_approuval_refused', {
                      name: recipient.name,
                      reason: recipient.rejectionReason ?? '',
                    })
                  : t('Documents-directory.sign_request_refused', {
                      name: recipient.name,
                      reason: recipient.rejectionReason ?? '',
                    }),
              date: recipient.signedAt,
              dateFormat: 'date-time',
            } as Activity,
          ]
        : []),
    ],
  )

  const activities: Activity[] = [
    ...(signRequest?.id
      ? [
          {
            id: signRequest?.id + 'sent',
            icon: 'Envelope',
            iconWeight: 'bold',
            iconClassName: 'text-blue-500',
            dotClassName: '!bg-blue-100',
            verticalLineClassName: '!bg-blue-100',
            title: t('Documents-directory.sign_request_sent'),
            date: signRequest?.createdAt,
            dateFormat: 'date-time',
          } as Activity,
        ]
      : []),
    ...(recipientActivities?.flat() || []),
    ...(signRequest?.status === 'COMPLETED'
      ? [
          {
            id: signRequest?.id + 'completed',
            icon: 'CheckSquareOffset',
            iconClassName: 'text-green-500',
            iconWeight: 'duotone',
            dotClassName: '!bg-green-100',
            title: t('Documents-directory.document_signed'),
            description: t('Documents-directory.document_signed_all'),
            date: signRequest?.updatedAt,
            dateFormat: 'date-time',
          } as Activity,
        ]
      : []),
  ]

  return (
    <div
      className={cx(
        'relative min-h-[60vh] overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm',
        className,
      )}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white via-white to-zinc-50" />
      <div className="relative p-4 sm:p-6">
        <ActivityBar
          locale={locale}
          activities={activities}
          className="w-full rounded-xl bg-white/70 p-4 shadow-sm ring-1 ring-zinc-200/60 backdrop-blur sm:p-5"
        />
      </div>
    </div>
  )
}
